/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { useUserEventsByTypesStream } from '@/api';
import { CONTENT_ACTIVITY_VIEWS, CONTENT_ACTIVITY_FETCH_EVENTS } from '@/constants/device';
import { DashboardIcon } from '../Common/Icons';
import useUserInfo from '../Hooks/useUserInfo';
import { aggregate } from './ContentActivityUtils';
import ListContentActivity from './ListContentActivity.component';

const DEFAULT_MAX_DAYS = 30;
const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars) || {};
const configuredHistoryDays = Number(ENV_VARS.CONTENT_ACTIVITY_NUMBER_OF_DAYS);
const CONTENT_ACTIVITY_HISTORY_DAYS =
  Number.isFinite(configuredHistoryDays) && configuredHistoryDays > 0
    ? configuredHistoryDays
    : DEFAULT_MAX_DAYS;

const LocalTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const ContentActivity = ({
  parentBread = [],
  onActionClick: parentActionClick,
  hubRecord,
  timezone,
  loginUserTimeformat,
} = {}) => {
  const trans = useSelector(translator);

  const useruuid = hubRecord?.useruuid;
  const userTimezone = (timezone ? timezone.split(' ')[0] : '') || LocalTimezone;
  const { data: userInfo = {} } = useUserInfo({ useruuid });

  const [activeView, setActiveView] = useState(CONTENT_ACTIVITY_VIEWS.SUMMARY);

  const windowBounds = useMemo(() => {
    const endMoment = moment().tz(userTimezone).endOf('day');
    const startMoment = endMoment
      .clone()
      .subtract(CONTENT_ACTIVITY_HISTORY_DAYS - 1, 'days')
      .startOf('day');
    return {
      fromMs: startMoment.valueOf(),
      toMs: endMoment.valueOf(),
      startISO: startMoment.clone().utc().format(),
      endISO: endMoment.clone().utc().format(),
    };
  }, [userTimezone]);

  // History rows render as events stream in; the summary cards and charts are aggregates, so they
  // wait for the complete result (`loading` stays true until the stream has finished).
  const {
    data: rawEvents,
    isLoading: isWaitingForRows,
    isStreaming,
    isPartial,
  } = useUserEventsByTypesStream({
    useruuid,
    eventTypes: CONTENT_ACTIVITY_FETCH_EVENTS,
    startDate: windowBounds.startISO,
    endDate: windowBounds.endISO,
  });
  const isLoading = isWaitingForRows || isStreaming;

  const model = useMemo(() => {
    const received = aggregate(rawEvents, userTimezone, windowBounds);
    if (!isPartial) return received;
    // The download failed part-way: keep the received history rows, but don't show summary cards
    // or charts computed from incomplete data (same as a failed request).
    return { ...aggregate([], userTimezone, windowBounds), historyByDate: received.historyByDate };
  }, [rawEvents, userTimezone, windowBounds, isPartial]);

  const header = (parentBread || []).concat(
    parentActionClick
      ? {
          label: trans('CONTENTACTIVITY'),
          onClick: parentActionClick(null, null),
        }
      : {
          label: trans('DASHBOARD'),
          icon: <DashboardIcon />,
        }
  );

  return (
    <ListContentActivity
      parentBread={header}
      model={model}
      loading={isLoading}
      historyIncomplete={isPartial}
      activeView={activeView}
      setActiveView={setActiveView}
      timezone={userTimezone}
      loginUserTimeformat={loginUserTimeformat}
      userInfo={userInfo}
    />
  );
};

ContentActivity.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  onActionClick: PropTypes.func,
  hubRecord: PropTypes.object,
  timezone: PropTypes.string,
  loginUserTimeformat: PropTypes.object,
};

export default ContentActivity;
